/**
 * Bot pacing for local games: brisk but readable, so each draw, flip and
 * placement still shows. Base ~0.55s with jitter (0.42-0.66s), a little longer
 * before swapping into a face-down slot, quicker during the simultaneous tee
 * off, and a short breath after the human's own move. `speed` scales
 * everything (?speed=0.2 for testing).
 *
 * 2026-11: every value is 0.6x the original (0.7-1.1s per move, tee off
 * 0.35-0.9s, +150ms hidden swap, +350ms after the human), which read as slow.
 * The worker's BOT_MOVE_DELAY_MS (online bots) took the same cut, 900 to 540.
 */
import type { Action, GameState } from '../../engine';

export interface PaceInput {
  action: Action;
  state: GameState;
  /** The previous move was the human's. */
  afterHuman: boolean;
  speed: number;
  /** 0..1 random source. */
  rand: () => number;
}

export const PACE = {
  min: 420,
  max: 660,
  teeOffMin: 210,
  teeOffMax: 540,
  hiddenSwapExtra: 90,
  afterHuman: 210,
};

export function botDelay({ action, state, afterHuman, speed, rand }: PaceInput): number {
  let ms: number;
  if (action.type === 'flipInitial') {
    ms = PACE.teeOffMin + rand() * (PACE.teeOffMax - PACE.teeOffMin);
  } else {
    ms = PACE.min + rand() * (PACE.max - PACE.min - PACE.hiddenSwapExtra);
    if (action.type === 'swap') {
      const slot = state.players[action.player]?.grid[action.slot];
      if (slot && !slot.faceUp) ms += PACE.hiddenSwapExtra;
    }
    if (afterHuman) ms += PACE.afterHuman;
  }
  return Math.round(ms * speed);
}
